import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FournisseurDTO} from '../../../../core/dtos/fournisseur-d-t-o';
import {FournisseurService} from '../../../../core/services/entities/fournisseur.service';
import {ResponseWrapper} from '../../../../core/suppliers/wrappers/response-wrapper';
import {forkJoin, Observable, Subject} from 'rxjs';
import {UtilsService} from '../../../../core/utils/utils.service';
import {SecteurDTO} from '../../../../core/dtos/secteur-dto';
import {SecteurFournisseurDTO} from '../../../../core/dtos/secteurfournisseur-dto';
import {MSG_KEY, MSG_SEVERITY} from '../../../../core/constants';
import {AdresseDTO} from '../../../../core/dtos/adresse-dto';
import {GestionFournisseurService} from '../../../../core/services/gestion-fournisseurs/gestion-fournisseur.service';
import {ObjectDTO} from '../../../../core/dtos/object-dto';
import {Auth2Service} from '../../../../core/services/security/auth2.service';
import {SiteDTO} from "../../../../core/dtos/site-dto";
import {ToastService} from "../../../../core/services/technique/toast.service";
import {GraphQLService} from "../../../../core/services/technique/graphql.service";
import {LookUpComponent} from "../../../../shared/ui/lookup/lookup.component";
import {TagBoxComponent} from "../../../../shared/ui/tag-box/tag-box.component";
import {DepartementDTO} from "../../../../core/dtos/departement-dto";
import {takeUntil} from "rxjs/operators";
import {confirm} from "devextreme/ui/dialog";
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';

@Component({
    selector: 'yo-filiale-form-dialog',
    templateUrl: './filiale-form-dialog.component.html',
    styleUrls: ['./filiale-form-dialog.component.scss'],
    standalone: false
})
export class FilialeFormDialogComponent implements OnInit, OnDestroy {

  public readonly ButtonThemeEnum = ButtonThemeEnum;

  @ViewChild("siteLookup") siteLookup: LookUpComponent;
  @ViewChild("secteursTagbox") secteursTagBox: TagBoxComponent;

  @Input() displayDialog: boolean;
  @Output() onClose = new EventEmitter<boolean>();

  sitesList: SiteDTO[];
  selectedSite: number;
  filiale: FournisseurDTO;
  secteurList: SecteurDTO[] = [];
  departementList: DepartementDTO[];
  departementListEnriched: any[] = [];
  editSelectedSecteurs: number[];

  isCreateMode: boolean;

  title: string = "Création d'une Filiale";

  selectedDepartements: number[] = [];

  private destroy$ = new Subject<void>();

  constructor(public auth2Svc: Auth2Service,
              private fournisseurSvc: FournisseurService,
              private gestionFournisseurSvc: GestionFournisseurService,
              private utils: UtilsService,
              private toastSvc: ToastService,
              private graphQlSvc: GraphQLService) {
  }

  ngOnInit(): void {
    this.sitesList = this.auth2Svc.utilisateur.siteListLocaux;

    forkJoin({
      secteurs: this.initSecteurs(),
      departements: this.initDepartements(),
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe(({secteurs, departements}) => {
      this.departementList = departements.allDepartement;
      this.departementListEnriched = [...this.departementList]
        .sort((a, b) => a.code.localeCompare(b.code))
        .map(dep => ({...dep, regionNom: dep.region?.nom, regionCode: dep.region?.code, nom: `${dep.code} - ${dep.nom}`}));
      this.secteurList = secteurs.allSecteurs;

      this.initEdit();
      this.initCreate();
    });
  }

  initDepartements = () => {
    return this.graphQlSvc.sendQuery(`
      {
        allDepartement {
          id,
          code,
          nom,
          region {
            id,
            code,
            nom
          }
        }
      }
    `);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initSecteurs = (): Observable<any> => {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
      {
        allSecteurs(filters: {
          siteIds: [${idsSites}]
        }) {
            id,
            libelle,
            site {
                id,
                libelle,
            },
        }
      }
    `);
  }

  initEdit = () => {
    this.fournisseurSvc.editFiliale$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: FournisseurDTO) => {
        this.isCreateMode = false;
        this.filiale = data;
        this.title = this.canEdit() ? `Modification de la Filiale ${data.libelle}` : `Filiale ${data.libelle}`;
        this.selectedSite = this.filiale.site.id;
        this.initSelectedSecteurs();
        this.initSelectedDepartements();
      });
  };

  initCreate = () => {
    this.fournisseurSvc.createFiliale$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: FournisseurDTO) => {
        this.editSelectedSecteurs = [];
        this.selectedSite = data.site.id;
        this.isCreateMode = true;
        this.title = "Création d'une Filiale";
        this.filiale = new FournisseurDTO();
        this.filiale.adresse = new AdresseDTO(data.site);
        this.filiale.actif = true;
        this.filiale.francoDePortActif = false;
        //parent
        this.filiale.parent = new ObjectDTO();
        this.filiale.parent.id = data.id;
        this.filiale.parent.code = data.code;
        this.filiale.parent.libelle = data.libelle;
        this.filiale.parent.actif = data.actif;

        //filiale
        this.filiale.site = data.site;
        this.filiale.libelle = data.libelle;
        this.filiale.id = 0;
        this.filiale.francoDePortTva = 20;
        this.filiale.francoDePortCout = 0;
      });
  };

  closeDialog = (isFromSavingBtn: boolean) => {
    this.displayDialog = false;
    this.onClose.emit(isFromSavingBtn);
  };

  canEdit = (): boolean => {
    return this.isCreateMode || this.auth2Svc.isSiteLocal(this.filiale?.site.id);
  };

  getHeaderIcon = (): string => {
    if (this.isCreateMode) {
      return 'plus';
    }
    return this.canEdit() ? 'edit' : 'eye';
  };

  initSaveFiliale = () => {
    if (this.isCreateMode) {
      this.filiale.site = {id: this.siteLookup.getSelectedItem().id} as SiteDTO;
    }

    this.filiale.idFournisseur = null;

    // valeurs par défaut du franco de port si les champs sont vides
    this.filiale.francoDePortCout = this.filiale.francoDePortCout || 0;
    this.filiale.francoDePortTva = this.filiale.francoDePortTva || 20;

    this.filiale.adresse.entityName = this.fournisseurSvc.getEntityName();

    this.filiale.secteurFournisseurList = this.secteursTagBox.getSelectedItems()
      .map(secteur => new SecteurFournisseurDTO(this.filiale.id, secteur));

    this.saveFiliale();
  }

  saveFiliale = async () => {
    this.utils.announceSubjectBlockui(true);
    this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.INFO, `Cette opération peut prendre du temps`);

    let res = false;
    if(!this.isCreateMode) {
      const question = 'Souhaitez-vous réinitialiser tous les départements au niveau des articles associés à ce fournisseur ?';
      res = await confirm(question, "Mise à jour des articles associés");
    }

    this.gestionFournisseurSvc.saveFournisseur(this.filiale, res).subscribe(response => {
      this.utils.announceSubjectBlockui(false);
      if (!this.utils.isResponseSupplierError(response)) {
        //annoncer que la fiche d'identite a été sauvegardée. La grille des fournisseurs doit être abonnée pour faire la mise à jour
        this.filiale = response.one;
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `La filiale ${this.filiale.libelle} a bien été enregistrée`);
        this.fournisseurSvc.announceEditFiliale(this.filiale);
        this.refreshFournisseurGrid(this.filiale);
        this.closeDialog(true);
      }
    });
  };

  private initSelectedSecteurs = () => {
    this.editSelectedSecteurs = this.filiale.secteurFournisseurList.map(secteur => secteur.secteur.id);
  };

  private initSelectedDepartements = () => {
    this.filiale.departements = this.departementList.filter(item =>
      this.filiale.departements.find(item2 => item2.id == item.id));
    this.selectedDepartements = this.filiale.departements.map(dep => dep.id);
  };

  isFormValid = (): boolean => {
    // en création : sans site choisi, formulaire invalide et on n'interroge pas la tagbox
    if (this.isCreateMode && !this.siteLookup?.getSelectedItem()) {
      return false;
    }
    const secteurValid = this.secteursTagBox?.getSelectedItems().length !== 0;
    const codeValid = this.isCreateMode || !!this.filiale?.code;
    return secteurValid && !!this.filiale?.libelle && codeValid;
  }

  private refreshFournisseurGrid = filiale => {
    this.gestionFournisseurSvc.getFournisseuById(this.utils.isNullOrEmpty(filiale) ? 0 : filiale.parent.id).subscribe((response: ResponseWrapper<FournisseurDTO>) => {
      const filialeParent = response.one;
      this.gestionFournisseurSvc.announceRefreshGrid(filialeParent);
    });
  }

  groupDepartementByRegion = (dep: DepartementDTO): string => dep.region.nom;

  onDepartementsChange = (selectedIds: number[]): void => {
    this.selectedDepartements = selectedIds.length ? selectedIds : null;
    this.filiale.departements = selectedIds.length
      ? this.departementList.filter(dep => selectedIds.find(id => id === dep.id))
      : null;
  };
}
